import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { forkJoin, of, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, exhaustMap, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import * as _ from 'underscore';
import { ConfirmDailogueComponent } from 'src/app/shared/components/dailogue/confirm-dailogue.component';
import { Api } from 'src/app/shared/constants/api';
import { APP_ROUTES } from 'src/app/shared/constants/approutes';
import { Message } from 'src/app/shared/constants/message';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { IPermissionWithShow } from 'src/app/shared/interface';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ICountryWiseServiceRequirementDetail } from '../country-wise-service-requirement.interface';
import { IServiceCriteria } from '../../service/service.interface';

@Component({
    selector: 'app-country-wise-service-requirement-form',
    templateUrl: './country-wise-service-requirement-form.component.html',
    styleUrls: ['./country-wise-service-requirement-form.component.less'],
    standalone: false
})
export class CountryWiseServiceRequirementFormComponent implements OnInit, OnDestroy {

  countryWiseServiceRequirementForm: FormGroup;
  countries: any[] = [];
  businessSegments: any[] = [];

  filteredCountries: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  filteredBusinessSegments: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  filteredServices: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  initialServices: any[] = [];

  countrySearchCtrl: FormControl = this.formBuilder.control('');
  bsSearchCtrl: FormControl = this.formBuilder.control('');
  serviceSearchCtrl: FormControl = this.formBuilder.control('');

  addPermission: boolean = false;
  editPermission: boolean = false;
  disableSubmit: boolean = false;
  isEditedData: boolean = false;

  viewLog = this.formBuilder.control(false);

  @Input() searching: boolean = false;
  onDestroy = new Subject<void>();

  duplicateServiceIndices: Set<number> = new Set();

  constructor(private formBuilder: FormBuilder, private activatedRoute: ActivatedRoute,
    private router: Router, private utilityService: UtilityService,
    private service: DataService, private permissionService: PermissionService,
    private logger: NGXLogger, private dialog: MatDialog) { }

  ngOnInit(): void {
    this.getAllPermissions();
    this.initializeForm();
    this.activatedRoute.params.subscribe((param: Params) => {
      if (+param['id']) {
        this.id.setValue(+param['id']);
      }
    });
    this.loadDropdownsAndInit();
    this.filterCountries();
    this.filterBusinessSegments();
    this.filterRowServices();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  initializeForm() {
    this.countryWiseServiceRequirementForm = this.formBuilder.group({
      id: [],
      countryId: [null, [Validators.required]],
      businessSegmentId: [null, [Validators.required]],
      countryWiseServiceRequirementDetails: this.formBuilder.array([this.buildDetailRow()])
    });
  }

  buildDetailRow(data?: ICountryWiseServiceRequirementDetail): FormGroup {
    return this.formBuilder.group({
      id: [data?.id || null],
      serviceId: [data?.serviceId || null, [Validators.required]],
      notRequired: [data?.notRequired ?? false],
      displayCreatedBy: [{ value: data?.displayCreatedBy || null, disabled: true }],
      displayCreatedDate: [{ value: data?.displayCreatedDate || null, disabled: true }]
    });
  }

  trackByControl(_index: number, control: AbstractControl): AbstractControl { return control; }

  trackByEntry(_index: number, entry: { index: number; control: AbstractControl }): AbstractControl {
    return entry.control;
  }

  get detailEntries(): { index: number; control: AbstractControl }[] {
    return this.details.controls.map((control, index) => ({ index, control }));
  }

  addRow() {
    const topRow = this.details.at(0);
    if (!topRow.get('serviceId').value) {
      this.utilityService.notifier(Message.CWSR_SELECT_SERVICE_BEFORE_ADD, 'warning');
      return;
    }
    this.details.insert(0, this.buildDetailRow());
    this.countryWiseServiceRequirementForm.markAsDirty();
  }

  removeRow(index: number) {
    if (this.details.length === 1) {
      this.utilityService.notifier(Message.CWSR_AT_LEAST_ONE_ROW_REQUIRED, 'warning');
      return;
    }
    this.details.removeAt(index);
    this.checkDuplicateServices();
    this.countryWiseServiceRequirementForm.markAsDirty();
  }

  checkDuplicateServices() {
    this.duplicateServiceIndices.clear();
    const seen = new Map<number, number>();
    this.details.controls.forEach((ctrl, i) => {
      const serviceId = ctrl.get('serviceId')?.value;
      if (!serviceId) return;
      if (seen.has(serviceId)) {
        this.duplicateServiceIndices.add(i);
        this.duplicateServiceIndices.add(seen.get(serviceId));
      } else {
        seen.set(serviceId, i);
      }
    });
  }

  onSave() {
    this.countryWiseServiceRequirementForm.markAllAsTouched();
    this.checkDuplicateServices();
    if (this.duplicateServiceIndices.size > 0) {
      this.utilityService.notifier(Message.CWSR_DUPLICATE_SERVICE, 'error');
      return;
    }
    if (this.countryWiseServiceRequirementForm.valid) {
      this.isEditedData = true;
      this.openDialog(this.id.value ? Message.SAVE_OPEN_DAILOG_IN_EDIT : Message.SAVE_OPEN_DAILOG);
    } else {
      this.utilityService.notifier(Message.CWSR_FILL_REQUIRED_FIELDS, 'error');
    }
  }

  saveModule() {
    this.countryWiseServiceRequirementForm.markAllAsTouched();
    const method = this.id.value ? 'put' : 'post';
    const successMsg = this.id.value
      ? Message.COUNTRY_WISE_SERVICE_REQUIREMENT_UPDATED
      : Message.COUNTRY_WISE_SERVICE_REQUIREMENT_SAVED;
    if (this.countryWiseServiceRequirementForm.valid) {
      this.countryWiseServiceRequirementForm.markAsUntouched();
      this.service[method](Api.COUNTRY_WISE_SERVICE_REQUIREMENT, this.countryWiseServiceRequirementForm.getRawValue()).subscribe(() => {
        this.utilityService.notifier(successMsg, 'success');
        this.router.navigate([APP_ROUTES.COUNTRY_WISE_SERVICE_REQUIREMENT]);
      });
    }
  }

  onCancel() {
    if (this.countryWiseServiceRequirementForm.dirty) {
      this.isEditedData = false;
      this.openDialog(Message.CANCEL_OPEN_DAILOG_IN_EDIT);
    } else {
      this.router.navigate([APP_ROUTES.COUNTRY_WISE_SERVICE_REQUIREMENT]);
    }
  }

  private openDialog(message?: string) {
    const dialogRef = this.dialog.open(ConfirmDailogueComponent, {
      data: message,
      maxWidth: '300px'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === false) { this.isEditedData = false; return; }
      if (!this.isEditedData) {
        this.router.navigate([APP_ROUTES.COUNTRY_WISE_SERVICE_REQUIREMENT]);
      } else {
        this.saveModule();
      }
    });
  }

  private getById() {
    if (this.id.value) {
      this.service.post(Api.COUNTRY_WISE_SERVICE_REQUIREMENT + '/getById', { id: this.id.value }).subscribe(res => {
        this.logger.info('CountryWiseServiceRequirement by Id', res);
        if (!this.editPermission) {
          this.countryWiseServiceRequirementForm.disable();
          this.disableSubmit = true;
        }
        this.countryWiseServiceRequirementForm.patchValue(res, { emitEvent: false });
        this.patchDetails(res);
        this.ensureSelectedServicesVisible();
      });
    }
  }

  private patchDetails(res: any) {
    this.details.clear();
    const isViewMode = this.countryWiseServiceRequirementForm.disabled;
    if (res.countryWiseServiceRequirementDetails && res.countryWiseServiceRequirementDetails.length > 0) {
      const sorted = [...res.countryWiseServiceRequirementDetails].sort((a: any, b: any) => (b.id || 0) - (a.id || 0));
      sorted.forEach((detail: ICountryWiseServiceRequirementDetail) => {
        const row = this.buildDetailRow(detail);
        if (isViewMode) {
          row.disable();
        }
        this.details.push(row);
      });
    } else {
      this.details.push(this.buildDetailRow());
    }
    this.countryWiseServiceRequirementForm.markAsPristine();
  }

  private loadDropdownsAndInit() {
    const serviceCriteria: IServiceCriteria = { size: 20, page: 0 };
    forkJoin({
      countries: this.service.get(Api.COUNTRY_FOR_DD),
      businessSegments: this.service.get(Api.BUSINESS_SEGMENT_FOR_DD),
      services: this.service.get(Api.SERVICE_MASTER, serviceCriteria)
    }).subscribe({
      next: ({ countries, businessSegments, services }) => {
        if (countries) {
          this.countries = countries;
          this.filteredCountries.next(this.countries);
        }
        if (businessSegments) {
          this.businessSegments = businessSegments;
          this.filteredBusinessSegments.next(this.businessSegments);
        }
        if (services?.contents) {
          this.initialServices = services.contents.slice();
          this.filteredServices.next(this.initialServices.slice());
        }
        this.getById();
      }
    });
  }

  private ensureSelectedServicesVisible() {
    const serviceIds: number[] = [...new Set(
      this.details.controls.map(r => r.get('serviceId')?.value).filter(Boolean) as number[]
    )];
    const missingIds = serviceIds.filter(id => !this.initialServices.some(s => s.id === id));
    if (!missingIds.length) return;

    forkJoin(missingIds.map(id => {
      const serviceCriteria: IServiceCriteria = { 'id.equals': id, size: 1, page: 0 };
      return this.service.get(Api.SERVICE_MASTER, serviceCriteria);
    })).subscribe((responses: any[]) => {
      responses.forEach(res => {
        const item = res?.contents?.[0];
        if (item && !this.initialServices.some(s => s.id === item.id)) {
          this.initialServices.unshift(item);
        }
      });
      this.filteredServices.next(this.initialServices.slice());
    });
  }

  filterCountries() {
    this.countrySearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      exhaustMap(value => {
        const searchStr = (!value || Array.isArray(value)) ? '' : String(value).toLowerCase();
        const filtered = !searchStr
          ? this.countries.slice()
          : this.countries.filter(c => c.name?.toLowerCase().includes(searchStr));
        this.filteredCountries.next(filtered);
        this.searching = false;
        return of([]);
      })
    ).subscribe(() => {}, () => { this.searching = false; });
  }

  filterBusinessSegments() {
    this.bsSearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      exhaustMap(value => {
        const searchStr = (!value || Array.isArray(value)) ? '' : String(value).toLowerCase();
        const filtered = !searchStr
          ? this.businessSegments.slice()
          : this.businessSegments.filter(bs => bs.name?.toLowerCase().includes(searchStr));
        this.filteredBusinessSegments.next(filtered);
        this.searching = false;
        return of([]);
      })
    ).subscribe(() => {}, () => { this.searching = false; });
  }

  filterRowServices() {
    this.serviceSearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(value => {
        if (!value) {
          this.filteredServices.next(this.initialServices.slice());
          this.searching = false;
          return of([]);
        }
        const serviceCriteria: IServiceCriteria = { 'serviceDescription.contains': value, size: 20, page: 0 };
        return this.service.get(Api.SERVICE_MASTER, serviceCriteria).pipe(
          map(res => res?.contents || []),
          tap(results => {
            const newItems = (results as any[]).filter(r => !this.initialServices.some(s => s.id === r.id));
            this.initialServices.push(...newItems);
            const selectedIds = [...new Set<number>(this.details.controls.map(row => row.get('serviceId')?.value).filter(Boolean))];
            const hiddenSelected = selectedIds
              .filter((id: number) => !results.some((r: { id: number }) => r.id === id))
              .map((id: number) => this.initialServices.find(s => s.id === id))
              .filter((s: any) => s?.id && s?.serviceCode)
              .map((s: any) => ({ ...s, _hidden: true }));
            this.filteredServices.next([...results, ...hiddenSelected]);
            this.searching = false;
          })
        );
      })
    ).subscribe(() => {}, () => { this.searching = false; });
  }

  getSelectedName(list: any[], id: number): string {
    if (!id || !list) return '';
    return list.find(item => item.id === id)?.name || '';
  }

  getSelectedServiceDescription(id: number): string {
    if (!id) return '';
    const s = this.initialServices.find(item => item.id === id);
    return s?.serviceDescription || '';
  }

  getSaveBtnCondition(): boolean {
    if (this.disableSubmit
      || (!this.editPermission && this.id.value)
      || (!this.addPermission && !this.id.value)
      || !this.countryWiseServiceRequirementForm.dirty) {
      return false;
    }
    return true;
  }

  public getAllPermissions() {
    const permissionCodes = [
      PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_ADD_PERMISSION,
      PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_EDIT_PERMISSION
    ];
    const permissionsWithShowValue: IPermissionWithShow[] =
      this.permissionService.checkPermissions(permissionCodes, PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE);
    this.addPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_ADD_PERMISSION })?.show ? true : false;
    this.editPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_EDIT_PERMISSION })?.show ? true : false;
  }

  get id() { return this.countryWiseServiceRequirementForm.get('id'); }
  get countryId() { return this.countryWiseServiceRequirementForm.get('countryId'); }
  get businessSegmentId() { return this.countryWiseServiceRequirementForm.get('businessSegmentId'); }
  get details(): FormArray { return this.countryWiseServiceRequirementForm.get('countryWiseServiceRequirementDetails') as FormArray; }
}
